import React, { forwardRef } from 'react';
import { LuLoaderCircle } from 'react-icons/lu';
import { cx } from '../../utils/cx.js';

// Focus ring shared by every interactive primitive (keyboard users only).
export const FOCUS_RING = 'outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-night-900';

const VARIANTS = {
  primary: 'bg-brand-600 text-white shadow-sm hover:bg-brand-700 dark:hover:bg-brand-500',
  secondary: 'border border-gray-200 bg-white text-gray-700 shadow-sm hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:bg-night-800 dark:text-gray-200 dark:hover:bg-night-750 dark:hover:text-white',
  soft: 'bg-brand-50 text-brand-700 hover:bg-brand-100 dark:bg-brand-900 dark:text-brand-300 dark:hover:bg-brand-950',
  ghost: 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white',
  danger: 'bg-red-600 text-white shadow-sm hover:bg-red-700',
  'danger-ghost': 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10',
};

const SIZES = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-9 px-3.5 text-sm gap-2',
  lg: 'h-11 px-5 text-sm gap-2',
};

const ICON_SIZES = { sm: 'h-3.5 w-3.5', md: 'h-4 w-4', lg: 'h-4 w-4' };

/**
 * Button with the Insights look. `as` renders a router Link or an anchor with the same styles.
 * `icon` / `iconRight` take an icon component (e.g. LuPlus); `loading` swaps the icon for a spinner.
 */
const Button = forwardRef(({
  as: Component = 'button',
  variant = 'secondary',
  size = 'md',
  icon: Icon = null,
  iconRight: IconRight = null,
  loading = false,
  disabled = false,
  fullWidth = false,
  className = '',
  children,
  ...rest
}, ref) => {
  const isButton = Component === 'button';
  const iconClass = ICON_SIZES[size] || ICON_SIZES.md;

  return (
    <Component
      ref={ref}
      type={isButton ? rest.type || 'button' : undefined}
      disabled={isButton ? disabled || loading : undefined}
      aria-disabled={!isButton && disabled ? true : undefined}
      aria-busy={loading || undefined}
      className={cx(
        'inline-flex items-center justify-center rounded-lg font-medium whitespace-nowrap transition-colors cursor-pointer select-none',
        'disabled:cursor-not-allowed disabled:opacity-50',
        !isButton && disabled && 'pointer-events-none opacity-50',
        FOCUS_RING,
        VARIANTS[variant] || VARIANTS.secondary,
        SIZES[size] || SIZES.md,
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading
        ? <LuLoaderCircle className={cx(iconClass, 'animate-spin')} aria-hidden />
        : Icon && <Icon className={cx(iconClass, 'flex-shrink-0')} aria-hidden />}
      {children}
      {IconRight && <IconRight className={cx(iconClass, 'flex-shrink-0')} aria-hidden />}
    </Component>
  );
});

Button.displayName = 'Button';

export default Button;
